import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnInit,
} from "@angular/core";
import { MatCalendar } from "@angular/material/datepicker";
import { DateAdapter } from "@angular/material/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

@UntilDestroy()
@Component({
    selector: "date-picker-header",
    template: `
        <div class="date-picker-header">
            <div class="month-year-selectors">
                <ds-button type="black-link"
                           (clicked)="currentMonthClicked()">
                    <span>{{ monthButtonText }}</span>
                    <span class="fal dropdown-icon" [ngClass]="setChevronDirection()"></span>
                </ds-button>
            </div>
            <div class="month-selectors" test-id="month-controls">
                <ds-button type="black-link"
                           (clicked)="previousClicked()"
                           [disabled]="!previousEnabled()"
                           test-class="mat-calendar-previous-button">
                    <span class="fal fa-chevron-left prev-icon"></span>
                </ds-button>
                <ds-button type="black-link"
                           (clicked)="nextClicked()"
                           [disabled]="!nextEnabled()"
                           test-class="mat-calendar-next-button">
                    <span class="fal fa-chevron-right next-icon"></span>
                </ds-button>
            </div>
        </div>
    `,
    styles: [`
        @import "/src/styles/colors";

        .date-picker-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1rem 0.25rem 1.5rem 1.25rem;

            .dropdown-icon {
                padding-top: 0.15rem;
                margin-left: 0.25rem;
                width: 0.75rem;
                height: 0.75rem;
            }

            .prev-icon, .next-icon {
                width: 1rem;
                height: 1rem;
                color: $gray-8;
            }

            .prev-icon {
                margin-right: 1rem;
            }

            .month-year-selectors, .month-selectors {
                display: flex;

                ds-button {
                    display: flex;
                    align-items: baseline;
                }
            }
        }
    `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})

export class DatePickerHeaderComponent implements OnInit {

    constructor(private _dateAdapter: DateAdapter<Moment>,
                private _calendar: MatCalendar<Moment>,
                cdr: ChangeDetectorRef) {
        _calendar.stateChanges
            .pipe(untilDestroyed(this))
            .subscribe(() => cdr.markForCheck());
    }

    ngOnInit() {
        if (!this._calendar.minDate) {
            this._calendar.minDate = this._dateAdapter.addCalendarYears(this._dateAdapter.today(), -5);
        }
        if (!this._calendar.maxDate) {
            this._calendar.maxDate = this._dateAdapter.addCalendarYears(this._dateAdapter.today(), 5);
        }
    }

    currentMonthClicked(): void {
        this._calendar.currentView = this._calendar.currentView === "month" ? "year" : "month";
    }

    get monthButtonText(): string {
        return this._calendar.activeDate.format("MMM YYYY");
    }

    setChevronDirection(): string {
        return this._calendar.currentView !== "month" ? "fa-chevron-up" : "fa-chevron-down";
    }

    previousClicked(): void {
        this._calendar.activeDate = this._calendar.currentView === "month" ?
            this._dateAdapter.addCalendarMonths(this._calendar.activeDate, -1) :
            this._dateAdapter.addCalendarYears(this._calendar.activeDate, -1);
    }

    nextClicked(): void {
        this._calendar.activeDate = this._calendar.currentView === "month" ?
            this._dateAdapter.addCalendarMonths(this._calendar.activeDate, 1) :
            this._dateAdapter.addCalendarYears(this._calendar.activeDate, 1);
    }

    previousEnabled(): boolean {
        return !this._calendar.minDate ||
            !this._isSameView(this._calendar.activeDate, this._calendar.minDate);
    }

    nextEnabled(): boolean {
        return !this._calendar.maxDate ||
            !this._isSameView(this._calendar.activeDate, this._calendar.maxDate);
    }

    private _isSameView(date1: Moment, date2: Moment): boolean {
        if (this._calendar.currentView === "month") {
            return this._dateAdapter.getYear(date1) === this._dateAdapter.getYear(date2) &&
                this._dateAdapter.getMonth(date1) === this._dateAdapter.getMonth(date2);
        } else if (this._calendar.currentView === "year") {
            return this._dateAdapter.getYear(date1) === this._dateAdapter.getYear(date2);
        }
    }
}
